import { Row, Col, Flex, Divider } from 'antd';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useSelector } from 'react-redux';
import { GoogleMapsProvider } from '@/components/GoogleMapsProvider';
import { getMapStyles } from '@/common/GoogleMapStyle';
import type { RootState } from '@/redux-toolkit/store';

import CustomTitle from '@/components/CustomTitle';
import React, { CSSProperties, ReactNode, useMemo } from 'react';

interface Airline {
  name: string;
}

interface Address {
  locationType?: string;
  locationAddress?: string;
}

interface Facility {
  indicator: string;
  attributes: string;
}

interface UAContactInfo {
  Email?: string;
  Sales_email?: string;
  Sales_Email?: string;
  Phone?: string;
  Sales_Phone?: string;
  FIRMS_Code?: string;
}

interface TabData {
  facilityAddress?: string;
}

interface LufthansaAdresses {
  address_type: string;
  address: string;
  city: string;
}

export interface ContactItem {
  contact_type: string;
  phone_number: string | null;
  email: string | null;
}

export interface Warehouse {
  name?: string | null;
  address?: string | null;
  zip_code?: string | null;
  country?: string | null;
  email?: string | null;
  phone_number?: string | null;
}

interface AirportData {
  importAddress?: string;
  exportAddress?: string;
  address?: Address;

  portCode?: string;
  firmsCode?: string;
  IRS_Number?: string;
  phone_number?: string;

  facilityInfo?: Facility[];
  tabData?: TabData;

  // Lufthansa
  contacts?: ContactItem[];
  LufthansaAddress?: LufthansaAdresses[];

  // Legacy Lufthansa props (still in your code)
  importHandlingEmail?: string;
  importHandlingPhone?: string;
  exportHandlingEmail?: string;
  exportHandlingPhone?: string;
  transitHandlingEmail?: string;
  transitHandlingPhone?: string;
  sales?: string;
}

interface UAData {
  contactInfo?: UAContactInfo;
}

interface MapCenter {
  lat: number;
  lng: number;
}

interface AirportSidebarProps {
  airline: Airline;
  data: AirportData;
  UAdata?: UAData;
  center: MapCenter;
  containerStyle: CSSProperties;
}

interface ContactInfoItem {
  label: string;
  value: React.ReactNode;
}

// --- Types ---
interface Airline {
  name: string;
}

interface Address {
  locationType?: string;
  locationAddress?: string;
}

interface Facility {
  indicator: string;
  attributes: string;
}

interface TabData {
  facilityAddress?: string;
}

interface UAContactInfo {
  email?: string;
  sales_email?: string;
  phone_number?: string;
  phone_number_2?: string;
  firms_code?: string;
}

interface UAData {
  contactInfo?: UAContactInfo;
}

interface AirportData {
  importAddress?: string;
  exportAddress?: string;
  address?: Address;
  portCode?: string;
  firmsCode?: string;
  IRS_Number?: string;
  phone_number?: string;
  facilityInfo?: Facility[];
  tabData?: TabData;

  warehouses?: Warehouse[];
  // Lufthansa-specific
  sales?: string;
}

interface MapCenter {
  lat: number;
  lng: number;
}

interface InfoItemProps {
  label: string;
  value?: string | ReactNode;
  maxWidth?: string;
  isAirfrance?: boolean;
}

interface AddressSectionProps {
  title: string;
  value?: string;
  value2?: string;
  maxWidth?: string;
}

interface ContactInfoSectionProps {
  title: string;
  items: { label: string; value?: string | ReactNode }[];
  isAirfrance?: boolean;
}

interface AirportSidebarProps {
  airline: Airline;
  data: AirportData;
  UAdata?: UAData;
  center: MapCenter;
  containerStyle: CSSProperties;
}

// --- Components ---
export const InfoItem: React.FC<InfoItemProps> = ({
  label,
  value,
  maxWidth,
  isAirfrance = false,
}) => (
  <Flex vertical gap={4}>
    <CustomTitle
      text={label}
      fontSize={isAirfrance ? '16px' : '12px'}
      fontWeight='600'
      lineHeight='16px'
      color='var(--text-primary)'
      marginBottom='0'
      marginTop='0'
    />
    <div style={{ maxWidth }}>
      <CustomTitle
        text={value || '--'}
        fontSize='12px'
        fontWeight='400'
        lineHeight='20px'
        color='var(--text-light)'
        marginBottom='0'
        marginTop='0'
      />
    </div>
  </Flex>
);

const AddressSection: React.FC<AddressSectionProps> = ({
  title,
  value,
  value2,
  maxWidth = '180px',
}) => (
  <Flex vertical gap={4}>
    <CustomTitle
      text={title}
      fontSize='16px'
      fontWeight='600'
      lineHeight='20px'
      color='var(--text-primary)'
      marginBottom='0'
      marginTop='0'
    />
    <div style={{ maxWidth: maxWidth }}>
      <CustomTitle
        text={value || '--'}
        fontSize='12px'
        fontWeight='400'
        lineHeight='20px'
        color='var(--text-light)'
        marginBottom='0'
        marginTop='0'
      />
      {value2 && (
        <CustomTitle
          text={value2 || '--'}
          fontSize='12px'
          fontWeight='400'
          lineHeight='20px'
          color='var(--text-light)'
          marginBottom='0'
          marginTop='0'
        />
      )}
    </div>
  </Flex>
);

const ContactInfoSection: React.FC<ContactInfoSectionProps> = ({
  title,
  items,
}: {
  title: string;
  items: { label: string; value?: React.ReactNode }[];
}) => (
  <>
    <CustomTitle
      text={title}
      fontSize='16px'
      fontWeight='600'
      lineHeight='20px'
      color='var(--text-primary)'
      marginBottom='0'
      marginTop='0'
    />
    <Row gutter={[24, 24]}>
      {items.map((item, idx) => (
        <Col span={12} key={idx}>
          <InfoItem label={item.label} value={item.value as string} />
        </Col>
      ))}
    </Row>
  </>
);

const WarehousesSection: React.FC<ContactInfoSectionProps> = ({
  title,
  items,
  isAirfrance = false,
}) => (
  <>
    <CustomTitle
      text={title}
      fontSize='16px'
      fontWeight='600'
      lineHeight='20px'
      color='var(--text-primary)'
      marginBottom='0'
      marginTop='0'
    />
    <Row gutter={[24, 24]}>
      {items.map((item, idx) => (
        <Col span={20} key={idx}>
          <InfoItem
            label={item.label}
            value={item.value}
            isAirfrance={isAirfrance}
          />
        </Col>
      ))}
    </Row>
  </>
);

const AirportSidebar: React.FC<AirportSidebarProps> = ({
  airline,
  data,
  UAdata,
  center,
  containerStyle,
}: AirportSidebarProps) => {
  const isLufthansa = airline?.name === 'Lufthansa';
  const isAirfrance = airline?.name === 'Airfrance KLM';
  const isUnited = airline?.name === 'United Airlines';
  const darkModeEnabled = useSelector(
    (state: RootState) => state.appearance.darkModeEnabled
  );
  const mapStyles = useMemo(
    () => getMapStyles(darkModeEnabled),
    [darkModeEnabled]
  );

  const contactItems: ContactInfoItem[] =
    data?.contacts?.map((c) => ({
      label: c.contact_type,
      value: (
        <>
          {c.email ?? '-'}
          <br />
          {c.phone_number ?? '-'}
        </>
      ),
    })) ?? [];

  return (
    <Flex vertical gap={24} className='zl-company-info'>
      <GoogleMapsProvider>
        <div style={containerStyle}>
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={center ? center : { lat: 0, lng: 0 }}
            zoom={center ? 12 : 2}
            options={{ styles: mapStyles }}
          >
            <Marker position={center} />
          </GoogleMap>
        </div>
      </GoogleMapsProvider>

      <Flex vertical gap={24} className='zl-company-detail'>
        {/* Warehouses */}
        {isAirfrance && (
          <WarehousesSection
            title='Warehouses'
            items={[
              ...(data?.warehouses?.map((item) => ({
                label: item?.name || '--',
                value: (
                  <>
                    {item?.address} {item?.zip_code} {item?.country}
                    <br />
                    {item?.phone_number}
                    <br />
                    {item?.email}
                  </>
                ),
              })) ?? []),
            ]}
            isAirfrance={isAirfrance}
          />
        )}

        {/* Address Section */}
        {isLufthansa ? (
          <>
            {data?.LufthansaAddress?.map((item, index) => (
              <React.Fragment key={index}>
                <AddressSection
                  title={
                    item?.address_type === 'export'
                      ? 'Export address'
                      : 'Import address'
                  }
                  value={`${item.address} ${item.city}`}
                />
              </React.Fragment>
            ))}
          </>
        ) : (
          <>
            {!isAirfrance && (
              <AddressSection
                title={
                  isUnited
                    ? 'Facility address'
                    : (data?.address?.locationType as string)
                }
                value={data?.address?.locationAddress}
              />
            )}{' '}
          </>
        )}

        {/* Contact Info */}
        {isLufthansa && (
          <ContactInfoSection title='Contact Info' items={[...contactItems]} />
        )}

        {isUnited && (
          <ContactInfoSection
            title='Contact Info'
            items={[
              { label: 'Email', value: UAdata?.contactInfo?.email },
              {
                label: 'Sales Email',
                value: UAdata?.contactInfo?.sales_email || '--',
              },
              { label: 'Phone 1', value: UAdata?.contactInfo?.phone_number },
              { label: 'Phone 2', value: UAdata?.contactInfo?.phone_number_2 },
              { label: 'Firms Code', value: UAdata?.contactInfo?.firms_code },
            ]}
          />
        )}

        {/* Customs */}
        {!isAirfrance && (
          <Flex vertical gap={16}>
            <CustomTitle
              text='Customs'
              fontSize='16px'
              fontWeight='600'
              lineHeight='20px'
              color='var(--text-primary)'
              marginBottom='0'
              marginTop='0'
            />

            <Row gutter={[40, 24]} justify='space-between'>
              {/* {isUnited && (
                <Col xs={24} sm={12} md={12}>
                  <InfoItem
                    label='Port code'
                    value={data?.tabData?.facilityAddress}
                  />
                </Col>
              )} */}
              {data?.portCode && (
                <Col xs={24} sm={12} md={12}>
                  <InfoItem label='Port code' value={data.portCode} />
                </Col>
              )}

              {data?.firmsCode && (
                <Col xs={24} sm={12} md={12}>
                  <InfoItem label='FIRMS code' value={data.firmsCode} />
                </Col>
              )}
            </Row>

            {data?.IRS_Number && (
              <InfoItem label='AA/IRS Number' value={data.IRS_Number} />
            )}

            {data?.phone_number && (
              <InfoItem label='Phone' value={data.phone_number} />
            )}
          </Flex>
        )}

        {isAirfrance && (
          <>
            <Divider style={{ margin: 0 }} />
            {data?.contacts?.length > 0 ? (
              <>
                {data?.contacts?.map((address, index) => (
                  <React.Fragment key={index}>
                    <AddressSection
                      title='File a claim'
                      value={address?.email || '--'}
                      value2={address?.phone_number}
                      maxWidth='100%'
                    />
                  </React.Fragment>
                ))}
              </>
            ) : (
              <Flex vertical gap={8}>
                <p className='zl-heading-16'>File a claim</p>
                N/A
              </Flex>
            )}
            <Divider style={{ margin: 0 }} />
          </>
        )}

        {/* Facilities */}
        <Flex vertical gap={8}>
          <CustomTitle
            text={isAirfrance ? 'Servicing' : 'Facilities'}
            fontSize='16px'
            fontWeight='600'
            lineHeight='20px'
            color='var(--text-primary)'
            marginBottom='0'
            marginTop='0'
          />
          {(() => {
            const filtered =
              data?.facilityInfo?.filter((f) => f.indicator === 'Yes') || [];

            return filtered.length > 0 ? (
              <ul className='zl-facility-list'>
                {filtered.map((f, idx) => (
                  <li key={idx}>{f.attributes}</li>
                ))}
              </ul>
            ) : (
              <p>N/A</p>
            );
          })()}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default AirportSidebar;
